
import React, { useState, useRef, useEffect } from 'react';
import { supportService } from '../services/supportService.frontend.js';

/**
 * Enterprise Support Modal
 * Tabs: "New Ticket" | "My Tickets"
 * Detail View: Conversation thread for a specific ticket
 */
export default function SupportTicketModal({ user, onClose }) {
    const [activeTab, setActiveTab] = useState('new'); // 'new' | 'list'
    const [selectedTicketId, setSelectedTicketId] = useState(null); // If set, showing detail view
    const modalRef = useRef(null);

    // Close on outside click
    useEffect(() => {
        function handleClickOutside(event) {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                onClose();
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [onClose]);

    // Main Render Switch
    const renderContent = () => {
        if (selectedTicketId) {
            return (
                <TicketDetailView
                    ticketId={selectedTicketId}
                    onBack={() => setSelectedTicketId(null)}
                />
            );
        }

        return (
            <>
                {/* -- Tabs -- */}
                <div style={{ display: 'flex', borderBottom: '1px solid #374151', marginBottom: '16px' }}>
                    <button
                        onClick={() => setActiveTab('new')}
                        style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            borderBottom: activeTab === 'new' ? '2px solid #22c55e' : '2px solid transparent',
                            color: activeTab === 'new' ? '#fff' : '#9ca3af',
                            padding: '10px',
                            cursor: 'pointer',
                            fontWeight: 500
                        }}
                    >
                        New Ticket
                    </button>
                    <button
                        onClick={() => setActiveTab('list')}
                        style={{
                            flex: 1,
                            background: 'transparent',
                            border: 'none',
                            borderBottom: activeTab === 'list' ? '2px solid #22c55e' : '2px solid transparent',
                            color: activeTab === 'list' ? '#fff' : '#9ca3af',
                            padding: '10px',
                            cursor: 'pointer',
                            fontWeight: 500
                        }}
                    >
                        My Tickets
                    </button>
                </div>

                {/* -- Tab Content -- */}
                {activeTab === 'new' ? (
                    <NewTicketForm user={user} onSuccess={() => setActiveTab('list')} onClose={onClose} />
                ) : (
                    <TicketListView onSelectTicket={(id) => setSelectedTicketId(id)} />
                )}
            </>
        );
    };

    return (
        <div className="wallet-flyout-backdrop">
            <div ref={modalRef} className="wallet-flyout-panel" style={{ width: '450px', height: '600px', display: 'flex', flexDirection: 'column' }}>
                <div className="wallet-flyout-header" style={{ flexShrink: 0 }}>
                    <span>Support Center</span>
                    <button className="wallet-flyout-close" onClick={onClose}>×</button>
                </div>

                <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    {renderContent()}
                </div>
            </div>
        </div>
    );
}

// --- Sub-components ---

function NewTicketForm({ user, onSuccess, onClose }) {
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [priority, setPriority] = useState('Medium');
    const [imageFile, setImageFile] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handleSubmit = async (e) => {
        e.preventDefault();


        if (!title.trim()) {
            setError("Title is required");
            return;
        }

        if (!description.trim()) {
            console.warn("Description is empty");
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {

            const formData = new FormData();
            formData.append('title', title);
            formData.append('description', description);
            formData.append('priority', priority);
            if (user && user.email) formData.append('email', user.email);
            if (imageFile) formData.append('image', imageFile);

            await supportService.createTicket(formData);

            onSuccess(); // Switch to list view - component unmounts here
        } catch (err) {
            console.error("Ticket submission failed in handleSubmit:", err);
            setError("Failed to submit ticket: " + err.message);
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', color: '#ccc', marginBottom: '6px', fontSize: '13px' }}>Title</label>
                    <input
                        type="text"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Brief summary of the issue"
                        style={{ width: '100%', background: '#020617', border: '1px solid #374151', borderRadius: '6px', color: '#ddd', padding: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                </div>

                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', color: '#ccc', marginBottom: '6px', fontSize: '13px' }}>Issue Description</label>
                    <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Describe your issue..."
                        rows={5}
                        style={{ width: '100%', background: '#020617', border: '1px solid #374151', borderRadius: '6px', color: '#ddd', padding: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                    />
                </div>

                <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', color: '#ccc', marginBottom: '6px', fontSize: '13px' }}>Priority</label>
                    <select
                        value={priority}
                        onChange={(e) => setPriority(e.target.value)}
                        style={{ width: '100%', background: '#020617', border: '1px solid #374151', borderRadius: '6px', color: '#ddd', padding: '8px', fontSize: '13px', boxSizing: 'border-box' }}
                    >
                        <option value="Low">Low</option>
                        <option value="Medium">Medium</option>
                        <option value="High">High</option>
                        <option value="Critical">Critical</option>
                        <option value="Feature Request">Feature Request</option>
                    </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', color: '#ccc', marginBottom: '6px', fontSize: '13px' }}>Attachment</label>
                    <input
                        type="file" accept="image/*"
                        onChange={(e) => setImageFile(e.target.files[0])}
                        style={{ color: '#aaa', fontSize: '12px' }}
                    />
                </div>

                {error && <div className="wallet-flyout-error">{error}</div>}
            </div>

            <div className="wallet-flyout-actions" style={{ padding: '16px 20px', borderTop: '1px solid #1f2937', background: 'transparent', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button type="button" className="wallet-flyout-portal-btn" onClick={onClose} style={{ margin: 0 }}>Cancel</button>
                <button type="submit" className="wallet-flyout-save-btn" disabled={isSubmitting} style={{ margin: 0 }}>
                    {isSubmitting ? 'Submitting...' : 'Submit Ticket'}
                </button>
            </div>
        </form>
    );
}

function TicketListView({ onSelectTicket }) {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadTickets();
    }, []);

    const loadTickets = async () => {
        try {
            const data = await supportService.getTickets();
            if (Array.isArray(data)) {
                setTickets(data);
            } else {
                console.warn("Tickets API returned non-array:", data);
                setTickets([]);
            }
        } catch (e) {
            console.error("Failed to load tickets:", e);
            setTickets([]);
        } finally {
            setLoading(false);
        }
    };

    if (loading) return <div style={{ color: '#888', textAlign: 'center', marginTop: '20px' }}>Loading...</div>;

    if (tickets.length === 0) {
        return (
            <div style={{ textAlign: 'center', marginTop: '40px', color: '#666' }}>
                <div style={{ fontSize: '24px', marginBottom: '10px' }}>🎫</div>
                No tickets yet.
            </div>
        );
    }

    return (
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 16px 16px 16px' }}>
            {tickets.map(t => (
                <div
                    key={t.ticket_id}
                    onClick={() => onSelectTicket(t.ticket_id)}
                    style={{
                        background: '#1f2937',
                        border: '1px solid #374151',
                        borderRadius: '8px',
                        padding: '12px',
                        marginBottom: '10px',
                        cursor: 'pointer',
                        transition: 'background 0.2s'
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#374151'}
                    onMouseLeave={e => e.currentTarget.style.background = '#1f2937'}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                        <span style={{ color: '#fff', fontWeight: 600, fontSize: '14px' }}>
                            {t.title || "Support Request"}
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <span style={{
                                fontSize: '10px', color: '#666', border: '1px solid #333', padding: '2px 4px', borderRadius: '4px'
                            }}>
                                #{t.ticket_id.substring(0, 8)}
                            </span>
                            <StatusBadge status={t.status} />
                        </div>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#888' }}>
                        <span style={{ color: '#ccc', fontSize: '13px' }}>
                            {t.preview || t.timeline?.[0]?.message?.substring(0, 50) || "No Preview Available"}
                        </span>
                        <span>{new Date(t.created_at).toLocaleDateString()}</span>
                    </div>
                    <div style={{ marginTop: '4px', textAlign: 'right', fontSize: '11px', color: '#666' }}>
                        {t.priority} Priority
                    </div>
                </div>
            ))}
        </div>
    );
}

function TicketDetailView({ ticketId, onBack }) {
    const [ticket, setTicket] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [replyImage, setReplyImage] = useState(null);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        loadTicket();
    }, [ticketId]);

    const loadTicket = async () => {
        try {
            const data = await supportService.getTicket(ticketId);
            setTicket(data);
        } catch (e) {
            console.error(e);
        }
    };

    const handleSendReply = async () => {
        if (!replyText.trim()) return;
        setSending(true);
        try {
            const formData = new FormData();
            formData.append('message', replyText);
            if (replyImage) formData.append('image', replyImage);

            await supportService.replyToTicket(ticketId, formData);

            // Optimistic update (or just reload)
            await loadTicket();
            setReplyText('');
            setReplyImage(null);
        } catch (e) {
            console.error("Reply failed", e);
            alert("Failed to send reply");
        } finally {
            setSending(false);
        }
    };

    if (!ticket) return <div style={{ color: '#888', textAlign: 'center', padding: '20px' }}>Loading Case Details...</div>;

    // Sort timeline: Newest First
    const timeline = [...(ticket.timeline || [])].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    // Valid messages (exclude description if stored as first msg, but in backend we store description in timeline[0])
    // The "Case Overview" shows the description. "Activity" shows replies.
    // So we filter out the very first message (creation message) from Activity stream if desired, 
    // OR we show ALL activity. Screenshot shows replies separately? 
    // Screenshot conversation history starts with "Hello, Glad to hear..." (Reply).
    // The "Case Description" is shown above. So we should likely exclude the *original* message from the activity feed if it duplicates description.
    // Backend stores original description as first timeline event.
    const activityMessages = timeline.filter((msg, idx) => {
        // If timeline is sorted newest-first, the original message is the LAST element.
        // Let's verify ticket creation logic: `timeline: [{...message: description...}]`
        // So original message is indeed in timeline.
        // If we sort newest first, it will be at the end.
        return idx !== timeline.length - 1;
    });


    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: '#020617', borderRadius: '12px', overflow: 'hidden' }}>
            {/* Header: Back + Case # + Status */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #374151', padding: '16px 16px 12px 16px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <button
                        onClick={onBack}
                        style={{
                            background: 'transparent',
                            border: '1px solid #374151',
                            borderRadius: '6px',
                            color: '#9ca3af',
                            cursor: 'pointer',
                            marginRight: '12px',
                            width: '32px',
                            height: '32px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 0,
                            flexShrink: 0
                        }}
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M19 12H5M12 19l-7-7 7-7" />
                        </svg>
                    </button>
                    <div style={{ marginLeft: '12px', flex: 1 }}>
                        <span style={{ fontSize: '13px', fontWeight: 600, color: '#9ca3af', letterSpacing: '0.5px' }}>
                            CASE #{ticket.ticket_id.split('-')[0]}
                        </span>
                    </div>
                </div>
                <StatusBadge status={ticket.status} />
            </div>

            <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>

                <div style={{ marginBottom: '24px' }}>
                    <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#fff', marginBottom: '12px', marginTop: 0, lineHeight: '1.4' }}>
                        {ticket.title || "Support Request"}
                    </h2>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                        <div>
                            <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '11px' }}>Product</div>
                            <div style={{ color: '#fff', fontWeight: 500 }}>Alekhine AI</div>
                        </div>
                        <div>
                            <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '11px' }}>Priority</div>
                            <div style={{ color: ticket.priority === 'Critical' ? '#ef4444' : '#fff', fontWeight: 500 }}>
                                {ticket.priority}
                            </div>
                        </div>
                        <div>
                            <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '11px' }}>Date Created</div>
                            <div style={{ color: '#d1d5db' }}>{new Date(ticket.created_at).toLocaleDateString()}</div>
                        </div>
                        <div>
                            <div style={{ color: '#6b7280', marginBottom: '2px', fontSize: '11px' }}>Requester</div>
                            <div style={{ color: '#d1d5db' }}>{ticket.user_email}</div>
                        </div>
                    </div>
                </div>

                {/* Case Overview / Description */}
                <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#e5e7eb', marginBottom: '12px', borderBottom: '1px solid #1f2937', paddingBottom: '8px' }}>
                        Case Overview
                    </h3>
                    <div style={{ background: '#0f172a', padding: '16px', borderRadius: '8px', border: '1px solid #1e293b' }}>
                        {/* We use the FIRST message (chronologically) as description, which is the LAST in sortedTimeline logic? 
                            Wait, I sorted `timeline` variable above as Newest First. 
                            So original message is `timeline[timeline.length - 1]`.
                        */}
                        <div style={{ fontSize: '13px', color: '#cbd5e1', whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                            {timeline[timeline.length - 1]?.message || "No description provided."}
                        </div>

                        {/* Check for attachments on original message */}
                        {timeline[timeline.length - 1]?.attachments?.length > 0 && (
                            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: '1px dashed #334155' }}>
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginBottom: '8px' }}>Attachments</div>
                                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    {timeline[timeline.length - 1].attachments.map((url, i) => (
                                        <a key={i} href={url} target="_blank" rel="noopener noreferrer" style={{
                                            border: '1px solid #334155', borderRadius: '4px', padding: '4px', display: 'block'
                                        }}>
                                            <img src={url} alt="attachment" style={{ height: '60px', borderRadius: '2px' }} />
                                        </a>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Activity Feed */}
                <div>
                    <h3 style={{ fontSize: '13px', fontWeight: 600, color: '#e5e7eb', marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>Activity</span>
                        <span style={{ fontSize: '11px', fontWeight: 400, color: '#64748b' }}>Newest First</span>
                    </h3>

                    {/* Reply Box - Simplified */}
                    <div style={{ marginBottom: '24px' }}>
                        <textarea
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Type a reply..."
                            style={{
                                width: '100%', background: '#0f172a', border: '1px solid #334155', borderRadius: '6px',
                                color: '#e2e8f0', padding: '12px', minHeight: '100px', marginBottom: '8px', fontSize: '13px',
                                boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical'
                            }}
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <label style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#64748b', transition: 'color 0.2s' }}>
                                <input
                                    type="file" accept="image/*"
                                    onChange={(e) => setReplyImage(e.target.files[0])}
                                    style={{ display: 'none' }}
                                />
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                    <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                                </svg>
                                {replyImage ? <span style={{ color: '#10b981' }}>{replyImage.name}</span> : <span>Attach File</span>}
                            </label>
                            <button
                                onClick={handleSendReply}
                                disabled={sending || !replyText.trim()}
                                style={{
                                    background: sending ? '#334155' : '#2563eb', color: '#fff', border: 'none',
                                    borderRadius: '4px', padding: '8px 16px', fontSize: '12px', fontWeight: 600,
                                    cursor: sending ? 'not-allowed' : 'pointer',
                                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                }}
                            >
                                {sending ? 'Sending...' : 'Send Reply'}
                            </button>
                        </div>
                    </div>

                    {/* Messages List (excluding original) */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', paddingLeft: '8px', borderLeft: '2px solid #1f2937' }}>
                        {activityMessages.length === 0 && (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#64748b', fontSize: '13px' }}>
                                No additional activity yet.
                            </div>
                        )}
                        {activityMessages.map((msg) => (
                            <div key={msg.message_id} style={{ position: 'relative', paddingLeft: '24px' }}>
                                {/* Timeline Dot */}
                                <div style={{
                                    position: 'absolute', left: '-13px', top: '0',
                                    width: '24px', height: '24px', borderRadius: '50%',
                                    background: msg.author === 'agent' ? '#10b981' : '#3b82f6',
                                    border: '4px solid #020617',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    color: '#fff', fontSize: '10px', fontWeight: 600
                                }}>
                                    {msg.author === 'agent' ? 'A' : 'U'}
                                </div>

                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                                    <span style={{ fontWeight: 600, fontSize: '13px', color: '#e2e8f0' }}>
                                        {msg.author_name || (msg.author === 'agent' ? 'Support Agent' : 'You')}
                                    </span>
                                    <span style={{ fontSize: '11px', color: '#64748b' }}>
                                        {new Date(msg.timestamp).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                    </span>
                                </div>
                                <div style={{ color: '#cbd5e1', fontSize: '13px', whiteSpace: 'pre-wrap', lineHeight: '1.5' }}>
                                    {msg.message}
                                </div>

                                {msg.attachments?.length > 0 && (
                                    <div style={{ marginTop: '8px' }}>
                                        {msg.attachments.map((url, i) => (
                                            <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                                                <img src={url} alt="att" style={{ height: '48px', borderRadius: '4px', border: '1px solid #333' }} />
                                            </a>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

            </div>
        </div>
    );
}

function StatusBadge({ status }) {
    let color = '#9ca3af'; // gray
    let bg = '#374151';

    if (status === 'Open') { color = '#60a5fa'; bg = 'rgba(37, 99, 235, 0.2)'; } // blue
    else if (status === 'In Progress') { color = '#fbbf24'; bg = 'rgba(245, 158, 11, 0.2)'; } // amber
    else if (status === 'Resolved') { color = '#34d399'; bg = 'rgba(16, 185, 129, 0.2)'; } // green
    else if (status === 'Closed') { color = '#9ca3af'; bg = '#1f2937'; } // gray

    return (
        <span style={{
            background: bg,
            color: color,
            padding: '2px 8px',
            borderRadius: '99px',
            fontSize: '10px',
            fontWeight: 600,
            textTransform: 'uppercase'
        }}>
            {status}
        </span>
    );
}
